import { Link } from "react-router-dom";
import { Monitor, Database, Cloud, Zap, BarChart3, Store, Leaf, Network, ArrowRight } from "lucide-react";
import { getImageUrl } from "../lib/utils";
import { useTranslation } from "../i18n/LanguageContext";

const iconMap = { Monitor, Database, Cloud, Zap, BarChart3, Store, Leaf, Network };

const SoftwareSection = ({ software }) => {
  const { t } = useTranslation();

  if (!software || software.length === 0) return null;

  return (
    <section id="solutions" className="py-24 bg-[#0B1121]" data-testid="software-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-[0.3em] text-[#00CCC2] mb-4">{t("software_label")}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            <span className="gradient-text">{t("software_title")}</span>
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto">{t("software_subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {software.map((sw, index) => {
            const Icon = iconMap[sw.icon] || iconMap.Monitor;
            return (
              <Link to={`/logiciel/${sw.slug}`} key={sw.id}
                className={`group p-6 rounded-2xl bg-[#131B2F] border border-white/10 card-hover ${index === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
                data-testid={`software-card-${index}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-[#00CCC2]/20 to-[#2395E3]/20">
                    <Icon className="w-6 h-6 text-[#00CCC2]" />
                  </div>
                  <h3 className="text-xl font-semibold group-hover:text-[#00CCC2] transition-colors">{sw.name}</h3>
                </div>
                {sw.tagline && <p className="text-[#00CCC2] text-sm mb-2">{sw.tagline}</p>}
                <p className="text-slate-400 mb-4">{sw.description}</p>
                {sw.features && sw.features.length > 0 && (
                  <ul className="space-y-2 mb-4">
                    {sw.features.slice(0, index === 0 ? 4 : 2).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00CCC2]" />{feature}
                      </li>
                    ))}
                  </ul>
                )}
                {sw.technologies && sw.technologies.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4 pt-4 border-t border-white/10">
                    {sw.technologies.map((tech, i) => (
                      <span key={i} className="px-3 py-1 text-xs rounded-full bg-[#2395E3]/20 text-[#2395E3]">{tech}</span>
                    ))}
                  </div>
                )}
                {index === 0 && sw.hero_image_url && (
                  <div className="mt-4 rounded-xl overflow-hidden">
                    <img src={getImageUrl(sw.hero_image_url)} alt={sw.name} className="w-full h-48 object-cover" />
                  </div>
                )}
                <div className="flex items-center gap-2 text-[#00CCC2] mt-4 group-hover:gap-3 transition-all">
                  <span className="text-sm font-medium">{t("software_more")}</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SoftwareSection;
